/**
 * Back from the Upload Studio to Product sync, as the operator left it.
 *
 * A same-tab trip to the studio (a Large cell's "Place on platen", a Product
 * updates row's "Place") first hands the page's state to its next visit
 * (`leaveForStudio`): the filters, search, page, expanded rows and selection
 * go into the page session (`lib/page-session`) under `PRODUCT_SYNC_RETURN_KEY`,
 * the staged designs are already there (`useStagedDesigns(scope)`), and the
 * page session is kept. Back (the studio's back button, Save and next after
 * the last product, or the browser's) mounts the page again at the same URL:
 * it reads the snapshot once (only when the URL is the one it was left from)
 * and brings the clicked cell (`StudioFocus`) back into view.
 *
 * Router state carries `{ from, focus, locationId }` (plus `walk` from
 * Product updates): `locationId` is the floor the page was publishing to, so
 * the studio does not ask again.
 */
import { createContext, useContext } from 'react';
import type { ArtworkSide } from '../hooks/useVariantArtworkUpload';
import type { MappingFilter } from './shared-stock';
import type {
  ImportFilter,
  PapClashFilter,
  PapStatusFilter,
  ProductionFilter,
  SelectedProductLite,
  StatusFilter,
  SyncFilter,
} from './types';

/** The page session page of Product sync. */
export const PRODUCT_SYNC_SESSION_PAGE = 'product-sync';
/** The page session key of the snapshot Back restores. */
export const PRODUCT_SYNC_RETURN_KEY = 'return';

/** The cell a "Place on platen" was clicked in: Back brings it into view. */
export interface StudioFocus {
  productId: string;
  /** The colour row key (the colour in lower case, or `all`). */
  colour: string;
  /** `<platen>:<variant>`, e.g. `14x16:Large`. */
  platenVariantKey: string;
  side: ArtworkSide;
}

export function sameStudioFocus(a: StudioFocus | null | undefined, b: StudioFocus | null | undefined): boolean {
  if (!a || !b) return false;
  return (
    a.productId === b.productId && a.colour === b.colour && a.platenVariantKey === b.platenVariantKey && a.side === b.side
  );
}

/** The Products tab as it was left (the tab and the store SKU filter ride the URL). */
export interface ProductSyncReturnSnapshot {
  /**
   * The page's URL when it was left (pathname + search, as the studio link's
   * `from`): restored only on a visit to that same URL (Back), never on a
   * plain visit (the sidebar) or another tab of the page.
   */
  from: string;
  searchTerm: string;
  productionFilter: ProductionFilter;
  statusFilter: StatusFilter;
  syncFilter: SyncFilter;
  mappingFilter: MappingFilter;
  importFilter: ImportFilter;
  papStatusFilter: PapStatusFilter;
  papClashFilter: PapClashFilter;
  cursorHistory: Array<string | undefined>;
  expandedIds: string[];
  selectedProducts: Array<[string, SelectedProductLite]>;
  selectionTruncated: boolean;
  overwriteMode: 'skip_existing' | 'overwrite';
  focus: StudioFocus | null;
}

/** What the page offers its studio links (absent outside Product sync). */
export interface StudioReturnApi {
  /** A same-tab trip to the studio is starting: keep this page for Back. */
  leaveForStudio: (focus: StudioFocus | null) => void;
  /** The floor the page publishes to now, carried so the studio does not ask again. */
  locationId: string | null;
  /** The cell Back is bringing into view, until it is shown or the operator moves. */
  pendingFocus: StudioFocus | null;
  /** The matching link is on screen: true once, and it is no longer pending. */
  takeFocus: (focus: StudioFocus) => boolean;
}

export const StudioReturnContext = createContext<StudioReturnApi | null>(null);

export function useStudioReturn(): StudioReturnApi | null {
  return useContext(StudioReturnContext);
}
